import { Injectable } from '@angular/core';
import { ResponseContentType } from '@angular/http';
import { saveAs } from 'file-saver/FileSaver';
import { Observable, Subject } from 'rxjs';
import { JobExportParams } from '../../app/atoms/job-export-params/job-export-params.model';
import { VASRestService } from '../../core/services/api/vas-rest';
import { HTTP_METHODS } from '../../core/services/sync-up';
import { parseLocation } from '../../core/services/sync-up/helpers';
import { JobExport } from '../business-objects/job-export/job-export.model';

export interface JobExportRequest {
    export: {
        exportType: string,
        parameters: {
            query: { [key: string]: string | Array<string> },
            controls: Array<string>
        }
    };
}

export interface ExportServiceCreateArgs extends JobExportParams {
    accountId: string;
}

const createDefaults = (): ExportServiceCreateArgs => ({
    accountId: undefined,
    exportType: 'export',
    fromDate: undefined,
    toDate: undefined,
    dateControl: undefined,
    jobStatuses: [],
    controls: []
});

@Injectable()
export class ExportService {

    constructor(protected api: VASRestService) {

    }

    create(args: ExportServiceCreateArgs): Observable<JobExport> {

        const _args = {...createDefaults(), ...args};

        const query = {
            jobStatuses: _args.jobStatuses,
            [`filterGte[${_args.dateControl}]`]: _args.fromDate,
            [`filterLte[${_args.dateControl}]`]: _args.toDate
        };

        const body: JobExportRequest = {
            export: {
                exportType: _args.exportType,
                parameters: {
                    controls: _args.controls,
                    query
                }
            }
        };

        return this.api.createRequest(true)
            .setMethod(HTTP_METHODS.POST)
            .setPath(`/accounts/${args.accountId}/exports`)
            .setBody(body)
            .execute()
            .concatMap(response => {
                const location = parseLocation(response);
                return this.get(location.account, location.resourceId);
            });
    }

    get(accountId: string, exportId: string): Observable<JobExport> {
        return this.api.simpleGet(`/accounts/${accountId}/exports/${exportId}`)
            .map(response => response.json());
    }

    getAll(accountId: string): Observable<Array<JobExport>> {
        return this.api.simpleGet(`/accounts/${accountId}/exports`)
            .map(response => response.json());
    }

    remove(accountId: string, exportId: string): Observable<any> {
        return this.api.delete(`/accounts/${accountId}/exports/${exportId}`);
    }

    download(accountId: string, exportId: string): Observable<any> {
        return this.api.createRequest(true)
            .setPath(`/accounts/${accountId}/exports/${exportId}/content`)
            .setResponseType(ResponseContentType.Blob)
            .execute()
            .map(response => response.blob())
            .do(blob => {
                const exportBlob = new Blob([blob], {type: 'text/csv'});
                saveAs(exportBlob, 'jobs-export.csv');
            });
    }

    poll(accountId: string, exportId: string, timeout = 5 * 60 * 1000, pollDelay = 1000): Observable<any> {

        const subject = new Subject<any>();
        let pollInterval;

        const succeed = () => {
            clearInterval(pollInterval);
            clearInterval(timeoutInterval);
        };

        const fail = error => {
            clearInterval(timeoutInterval);
            clearInterval(pollInterval);
            subject.error(error);
        };

        const timeoutInterval = setTimeout(() => {
            fail(new Error('operation timed out'));
        }, timeout);

        pollInterval = setInterval(() => {
            this.get(accountId, exportId)
                .subscribe(
                    exportModel => {

                        if (exportModel.succeeded) {
                            succeed();
                        }

                        subject.next(exportModel);

                    }, fail);
        }, pollDelay);

        return subject;
    }
}



// WEBPACK FOOTER //
// ./src/vas/services/export.service.ts